import { Button, Box, Typography, CircularProgress } from '@mui/joy'
import { ClientLoadModel, Truck } from '@numeo/types-core'
import { useState, useEffect, useCallback } from 'react'
import { sendLoadInfo } from 'services/spotApi'
import { useDispatch } from 'react-redux'
import { addNotification } from 'store/notificationSlice'
import { Category } from 'hooks/useNotification'
import { Send, CheckCircle, HelpOutline } from '@mui/icons-material'
import { formatDuration } from './CostsCard'
import Tooltip from '@mui/joy/Tooltip'
import { AxiosError } from 'axios'
import axios from 'axios'
import { useAuth } from 'hooks/auth'
import { useTeamDriversFull } from 'hooks/team/useTeamDriversFull'

/**
 * SendLoadInfo component sends load information to the driver.
 * This includes:
 * - Load details
 * - Truck info
 */

// Type for route response
interface RouteResponse {
    routeId: string
    polyline: string
    duration: number
    length: number
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    tolls: any[]
}

interface Props {
    load: ClientLoadModel
    truck: Truck | null
    duration?: number
    deadheadMiles: number
    rate: number
}

export const SendLoadInfoButton: React.FC<Props> = ({ load, truck, duration: initialDuration, deadheadMiles, rate }) => {
    const { application } = useAuth()
    const [isSending, setIsSending] = useState(false)
    const [sendSuccess, setSendSuccess] = useState(false)
    const [duration, setDuration] = useState<number | undefined>(initialDuration)
    const dispatch = useDispatch()

    // Reset success state after 3 seconds
    useEffect(() => {
        let timer: NodeJS.Timeout
        if (sendSuccess) {
            timer = setTimeout(() => {
                setSendSuccess(false)
            }, 3000)
        }
        return () => {
            if (timer) clearTimeout(timer)
        }
    }, [sendSuccess])

    // Process toll data from API response
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const processTollsFromResponse = useCallback((responseTolls: any[]) => {
        if (!responseTolls || !Array.isArray(responseTolls)) return []

        return responseTolls.map((toll) => ({
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            total: toll.fares.reduce((sum: number, fare: any) => sum + (fare.price?.value || 0), 0),
            details: [
                {
                    countryCode: toll.countryCode,
                    tollSystemRef: toll.tollSystemRef,
                    tollSystem: toll.tollSystem,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    fares: toll.fares.map((fare: any) => ({
                        id: fare.id,
                        name: fare.name,
                        price: fare.price?.value || 0,
                        reason: fare.reason,
                        paymentMethods: fare.paymentMethods,
                    })),
                    tollCollectionLocations: toll.tollCollectionLocations,
                },
            ],
        }))
    }, [])

    // Fetch route data function
    const getPolylineForLoad = async () => {
        try {
            const response = await axios.get<{ success: boolean; data: RouteResponse }>(`/trucks/loads/route`, {
                params: {
                    loadId: load._id,
                    originLat: load.origin.latitude,
                    originLng: load.origin.longitude,
                    destinationLat: load.destination.latitude,
                    destinationLng: load.destination.longitude,
                    truckId: truck?._id,
                    projectName: application?.projectName,
                },
            })

            // Use the response data directly
            if (response.data?.success && response.data?.data) {
                const routeData = response.data.data

                // Process tolls data if available
                if (routeData.tolls && Array.isArray(routeData.tolls)) {
                    // Process tolls but don't store the result since it's not used
                    processTollsFromResponse(routeData.tolls)
                }

                // Set duration if available
                if (routeData.duration) {
                    setDuration(routeData.duration)
                }

                return routeData.duration || initialDuration
            }
            return initialDuration
        } catch (error) {
            console.error('Error fetching polyline:', error)
            return initialDuration
        }
    }

    // Check if the truck has an email address (either first or second driver). The message goes to their own
    // addresses and Telegram group, which only their whole record carries (Driver profiles access).
    const firstDriverId = truck?.drivers?.[0]?.toString()
    const secondDriverId = truck?.drivers?.[1]?.toString()
    const { drivers: truckDrivers } = useTeamDriversFull([firstDriverId, secondDriverId])
    const firstDriver = firstDriverId ? truckDrivers.find((driver) => String(driver.id) === firstDriverId) : undefined
    const secondDriver = secondDriverId ? truckDrivers.find((driver) => String(driver.id) === secondDriverId) : undefined
    const firstDriverEmail = firstDriver?.email || ''
    const secondDriverEmail = secondDriver?.email || ''
    const telegramChatId = firstDriver?.telegramGroup?.chatId || secondDriver?.telegramGroup?.chatId || ''
    const hasEmailOrTelegram = Boolean(firstDriverEmail || secondDriverEmail || telegramChatId)

    // Check which communication channels are available
    const hasEmail = Boolean(firstDriverEmail || secondDriverEmail)
    const hasTelegram = Boolean(telegramChatId)

    const handleSendLoadInfo = async () => {
        if (!hasEmailOrTelegram) {
            dispatch(
                addNotification({
                    id: '',
                    message: 'Truck has no driver email address',
                    type: 'error',
                    category: Category.LoadFinderTruck,
                    timestamp: Date.now(),
                    topic: 'temp',
                })
            )
            return
        }

        try {
            setIsSending(true)

            // First fetch the route data if needed
            let currentDuration = duration
            if (!load?.polyline || !load?.tolls || !duration) {
                currentDuration = await getPolylineForLoad()
            }

            // Prepare all the data needed for the email
            const routeDescription = `${load.origin.city}, ${load.origin.stateProv} to ${load.destination.city}, ${load.destination.stateProv}`
            const distanceMiles = Number(load.tripLength || 0)
            const ratePerMile = Number((rate / distanceMiles).toFixed(2))

            // Send all data to backend
            await sendLoadInfo({
                routeDescription,
                distanceMiles,
                deadheadMiles,
                duration: formatDuration(currentDuration),
                firstDriverEmail,
                secondDriverEmail,
                telegramChatId,
                originLat: load.origin.latitude,
                originLng: load.origin.longitude,
                destinationLat: load.destination.latitude,
                destinationLng: load.destination.longitude,
                rate,
                ratePerMile,
                weight: load.shipmentDetails.maximumWeightPounds,
                commodity: load.equipmentType,
            })

            // Set success state
            setSendSuccess(true)

            dispatch(
                addNotification({
                    id: '',
                    message: 'Load information sent successfully',
                    type: 'info',
                    category: Category.LoadFinderTruck,
                    timestamp: Date.now(),
                    topic: 'temp',
                })
            )
        } catch (error) {
            console.error('Failed to send load info:', error)
            dispatch(
                addNotification({
                    id: '',
                    message: error instanceof AxiosError ? error.response?.data?.message : 'Failed to send load information',
                    type: 'error',
                    category: Category.LoadFinderTruck,
                    timestamp: Date.now(),
                    topic: 'temp',
                })
            )
        } finally {
            setIsSending(false)
        }
    }

    return (
        <div>
            <Tooltip
                title={
                    !hasEmailOrTelegram ? (
                        <Box sx={{ p: 1, maxWidth: 300 }}>
                            <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <HelpOutline fontSize="small" /> No Communication Channels
                            </Typography>
                            <Typography level="body-sm">This truck has no driver email or Telegram connection. To enable notifications:</Typography>
                            <Typography level="body-sm" sx={{ mt: 1 }}>
                                ➔ Add driver email address in truck settings to enable email notifications.
                            </Typography>
                            <Typography level="body-sm" sx={{ mt: 1 }}>
                                ➔ Copy the connect command from truck info panel and send it to your Telegram group.
                            </Typography>
                        </Box>
                    ) : (
                        <Box sx={{ p: 1, maxWidth: 300 }}>
                            <Typography level="title-sm" sx={{ mb: 1, display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                <HelpOutline fontSize="small" /> Send Load Information
                            </Typography>
                            <Typography level="body-sm">
                                {hasEmail && hasTelegram
                                    ? 'Load information will be sent to driver email and Telegram group.'
                                    : hasEmail && !hasTelegram
                                      ? 'Load information will be sent to driver email only. To enable Telegram notifications:'
                                      : !hasEmail && hasTelegram
                                        ? 'Load information will be sent to Telegram group only. To enable email notifications:'
                                        : ''}
                            </Typography>

                            {hasEmail && !hasTelegram && (
                                <Typography level="body-sm" sx={{ mt: 1 }}>
                                    ➔ Copy the connect command from truck info panel and send it to your Telegram group to enable Telegram notifications.
                                </Typography>
                            )}

                            {!hasEmail && hasTelegram && (
                                <Typography level="body-sm" sx={{ mt: 1 }}>
                                    ➔ Add driver email address in truck settings to enable email notifications.
                                </Typography>
                            )}
                        </Box>
                    )
                }
                placement="top"
                arrow
                variant="soft"
                sx={{ maxWidth: 'none' }}
            >
                <span style={{ display: 'inline-block' }}>
                    <Button
                        variant="solid"
                        color={sendSuccess ? 'success' : 'primary'}
                        onClick={handleSendLoadInfo}
                        disabled={!hasEmailOrTelegram}
                        endDecorator={isSending ? <CircularProgress size="sm" sx={{ color: 'white' }} /> : sendSuccess ? <CheckCircle /> : <Send />}
                        sx={{
                            backgroundColor: isSending ? 'var(--joy-palette-primary-500)' : undefined,
                            color: isSending ? 'white' : undefined,
                            '&:hover': isSending
                                ? {
                                      backgroundColor: 'var(--joy-palette-primary-600)',
                                  }
                                : undefined,
                            pointerEvents: isSending ? 'none' : undefined,
                        }}
                    >
                        {isSending ? 'Sending' : sendSuccess ? 'Sent' : 'Send Load Info'}
                    </Button>
                </span>
            </Tooltip>
        </div>
    )
}
